import React, { useId } from 'react';
import { OverflowFinish } from '@/components/OverflowFinish';

// Overflow mark: a cup filled past the brim and pouring down the side, with a jet arcing out to the right
export const OverflowMark: React.FC<{ className?: string; animated?: boolean }> = ({ className = 'w-12 h-9', animated = true }) => {
  const id = useId().replace(/:/g, '');
  const milk = `url(#${id}-milk)`;
  return (
    <svg viewBox="0 0 64 48" className={className} aria-hidden="true">
      <defs>
        <linearGradient id={`${id}-milk`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="1" stopColor="#efe4d8" />
        </linearGradient>
        <linearGradient id={`${id}-cup`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#f78bc6" />
          <stop offset="1" stopColor="#d9468f" />
        </linearGradient>
      </defs>
      {/* the pool it's left behind */}
      <ellipse cx="22" cy="45" rx="15" ry="1.8" fill={milk} opacity="0.85" />
      {/* liquid swelling well above the rim */}
      <path d="M9.5 18c2-9 23-9 25 0v8.5a12.5 12.5 0 0 1-25 0z" fill={milk} />
      <path d="M8 17.5v9a14 14 0 0 0 28 0v-9" fill="none" stroke={`url(#${id}-cup)`} strokeWidth="3.4" strokeLinecap="round" />
      {/* pouring over the left rim and down */}
      <path d="M13 11.6c-3 .8-5.4 2.6-6.2 5.2c-.5 1.6-.4 3.4-.4 5.4v9.4a1.9 1.9 0 0 0 3.8 0v-9c0-2.6.4-5.6 2.8-8.6z" fill={milk} />
      <ellipse className={animated ? 'logo-drip' : undefined} cx="8.4" cy="35.6" rx="2" ry="2.5" fill="#ffffff" />
      {/* the squirt: a jet arcing out to the right */}
      <path className={animated ? 'logo-jet' : undefined} d="M33 13.5c6-7 15-9.5 22-5.5c4 2.3 6 6.5 6.5 11.5" fill="none" stroke="#9fdcff" strokeWidth="2.6" strokeLinecap="round" />
      <g className={animated ? 'logo-spray' : undefined} fill="#9fdcff">
        <circle cx="61.6" cy="24.2" r="1.9" />
        <circle cx="58.6" cy="28.6" r="1.4" opacity="0.8" />
        <circle cx="62.6" cy="31.4" r="1.1" opacity="0.65" />
        <circle cx="56.4" cy="22.8" r="1" opacity="0.6" />
      </g>
    </svg>
  );
};

// Large: the word with its animated finish. Medium (sidebar): the cup mark beside the word.
export const OverflowWordmark: React.FC<{ size?: 'md' | 'lg' }> = ({ size = 'lg' }) =>
  size === 'lg' ? (
    <OverflowFinish textClassName="text-[44px] lg:text-[56px]" />
  ) : (
    <span className="flex items-center gap-2">
      <OverflowMark className="w-11 h-8" />
      <span className="font-display font-medium tracking-tight text-ink text-2xl">
        Over<span className="italic text-her">flow</span>
      </span>
    </span>
  );

// Slow, blurred colour fields drifting behind the page
export const FluidBackground: React.FC = () => (
  <div className="fluid-bg" aria-hidden="true">
    <span className="fluid-blob fluid-blob--her" />
    <span className="fluid-blob fluid-blob--him" />
    <span className="fluid-blob fluid-blob--pearl" />
  </div>
);
